/**
 * Full customer report: entropy heat map, top 10 friction, condition worksheets, bridge card, session analysis.
 * Print-optimized for landscape PDF export.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Typography, Table, message, Input } from 'antd';
import { PrinterOutlined, DownloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import {
  getTopFriction,
  getBridgeCard,
  getQuestions,
  getBridgeSession,
  updateBridgeSession,
  downloadQuestionsAnswersExport,
  type BridgeCardItemWithDetails,
  type FrictionItemWithQuestion,
  type QuestionWithAnswer,
} from '../../services/localBridgeSessionApi';
import { fetchEntropyMapFromLocalApi } from '../../services/bridgeSessionDataAdapter';
import { BridgeCard } from '../../components/BridgeCard/BridgeCard';
import type { EntropyMapResponse } from '../../types/bridgeSession';
import type { BridgeCardObjective } from '../../types/bridgeCard';
import styles from './BridgeSessionReportPage.module.css';

const { Title, Text } = Typography;

function getOptionLabel(question: QuestionWithAnswer | undefined, score: number | undefined): string {
  if (score == null || !question?.options?.length) return score != null ? `Score ${score}` : '—';
  const opt = question.options.find((o) => o.score === score);
  return opt?.label ?? `Score ${score}`;
}

export const BridgeSessionReportPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [entropyMap, setEntropyMap] = useState<EntropyMapResponse | null>(null);
  const [frictionList, setFrictionList] = useState<FrictionItemWithQuestion[]>([]);
  const [bridgeCardItems, setBridgeCardItems] = useState<BridgeCardItemWithDetails[]>([]);
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [bridgeCardObjective, setBridgeCardObjective] = useState<BridgeCardObjective | null>(null);
  const [sessionAnalysis, setSessionAnalysis] = useState<string | null>(null);
  const [editingAnalysis, setEditingAnalysis] = useState(false);
  const [analysisDraft, setAnalysisDraft] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const [entropy, friction, cardItems, qList, objective, session] = await Promise.all([
        fetchEntropyMapFromLocalApi(sessionId),
        getTopFriction(sessionId, 10),
        getBridgeCard(sessionId),
        getQuestions(sessionId, 'current'),
        bridgeSessionService.getBridgeCardFromObjective(sessionId),
        getBridgeSession(sessionId),
      ]);
      setEntropyMap(entropy ?? null);
      setFrictionList(friction ?? []);
      setBridgeCardItems(cardItems ?? []);
      setQuestions(qList ?? []);
      setBridgeCardObjective(objective ?? null);
      setSessionAnalysis(session?.session_analysis ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadJson = async () => {
    if (!sessionId) return;
    const ok = await downloadQuestionsAnswersExport(sessionId);
    if (ok) message.success('Download started.');
    else message.error('Download failed.');
  };

  const startEditingAnalysis = () => {
    setAnalysisDraft(sessionAnalysis ?? '');
    setEditingAnalysis(true);
  };

  const cancelEditingAnalysis = () => {
    setEditingAnalysis(false);
    setAnalysisDraft('');
  };

  const saveSessionAnalysis = async () => {
    if (!sessionId) return;
    setSavingAnalysis(true);
    try {
      const updated = await updateBridgeSession(sessionId, { session_analysis: analysisDraft || undefined });
      if (updated) {
        setSessionAnalysis(updated.session_analysis ?? null);
        setEditingAnalysis(false);
        setAnalysisDraft('');
        message.success('Session analysis saved.');
      } else {
        message.error('Failed to save.');
      }
    } catch {
      message.error('Failed to save.');
    } finally {
      setSavingAnalysis(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !entropyMap && !bridgeCardObjective) {
    return (
      <div className={styles.wrapper}>
        <Card>
          <Text type="danger">{error}</Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => sessionId && navigate(`/bridge-session/${sessionId}`)}>
              Back to session
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const questionById = new Map(questions.map((q) => [q.question_id, q]));
  const sortedCardItems = [...bridgeCardItems].sort((a, b) => a.priority - b.priority);

  const getEntropyColor = (score: number): string => {
    if (score >= 8) return '#ff4d4f';
    if (score >= 6) return '#faad14';
    if (score >= 4) return '#fa8c16';
    return '#52c41a';
  };

  return (
    <div className={styles.reportPage}>
      <div className={styles.reportActions}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => sessionId && navigate(`/bridge-session/${sessionId}?step=bridge_card`)}>
          Open Bridge Session
        </Button>
        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          Print / Export to PDF
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleDownloadJson}>
          Download questions & answers (JSON)
        </Button>
        <Button onClick={() => sessionId && navigate(`/bridge-session/${sessionId}?step=define_conditions`)}>
          Edit conditions
        </Button>
      </div>

      <div className={styles.reportContent}>
        {/* 1. Entropy heat map */}
        <section className={styles.section}>
          <Title level={2}>Entropy heat map</Title>
          <div className={styles.entropyLegend} aria-label="Entropy scale">
            <span><span className={styles.legendSwatch} style={{ backgroundColor: getEntropyColor(1) }} /> 1 = Ideal</span>
            <span><span className={styles.legendSwatch} style={{ backgroundColor: getEntropyColor(7) }} /> 7 = High Chaos</span>
          </div>
          {entropyMap?.hierarchy && entropyMap.hierarchy.length > 0 ? (
            <div className={styles.entropyGrid}>
              {entropyMap.business_entropy_score != null && (
                <div
                  className={styles.entropyTile}
                  style={{ borderLeftColor: getEntropyColor(entropyMap.business_entropy_score) }}
                >
                  <Text strong>Business</Text>
                  <span className={styles.scoreBadge} style={{ background: getEntropyColor(entropyMap.business_entropy_score) }}>
                    {entropyMap.business_entropy_score}/10
                  </span>
                </div>
              )}
              {entropyMap.hierarchy.map((domainGroup) => (
                <div key={domainGroup.domain_id} className={styles.entropyDomain}>
                  <div
                    className={styles.entropyDomainHeader}
                    style={{ borderLeftColor: getEntropyColor(domainGroup.rollup_entropy_score ?? 0) }}
                  >
                    <Text strong>{domainGroup.domain_name}</Text>
                    {domainGroup.rollup_entropy_score != null && (
                      <span className={styles.scoreBadge} style={{ background: getEntropyColor(domainGroup.rollup_entropy_score) }}>
                        {domainGroup.rollup_entropy_score}/10
                      </span>
                    )}
                  </div>
                  <div className={styles.entropyFactors}>
                    {domainGroup.subdomains.map((sub) => (
                      <div
                        key={sub.artifact_item_id}
                        className={styles.entropyTile}
                        style={{ borderLeftColor: getEntropyColor(sub.entropy_score) }}
                      >
                        <Text>{sub.subdomain_name}</Text>
                        <span className={styles.scoreBadge} style={{ background: getEntropyColor(sub.entropy_score) }}>
                          {sub.entropy_score}/10
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Text type="secondary">No entropy data. Run Questions and Calculate first.</Text>
          )}
        </section>

        {/* 2. Top 10 friction */}
        <section className={styles.section}>
          <Title level={2}>Top 10 friction items</Title>
          {frictionList.length > 0 ? (
            <Table
              dataSource={frictionList}
              rowKey="question_id"
              pagination={false}
              size="small"
              columns={[
                { title: 'Domain', dataIndex: 'domain', key: 'domain', width: 100 },
                { title: 'Factor', dataIndex: 'factor', key: 'factor', width: 100 },
                { title: 'Question', dataIndex: 'text', key: 'text', ellipsis: true },
                { title: 'Score', dataIndex: 'score', key: 'score', width: 70 },
                { title: 'Adjusted friction', dataIndex: 'adjusted_friction', key: 'adjusted_friction', width: 120, render: (v: number) => v?.toFixed(1) },
              ]}
            />
          ) : (
            <Text type="secondary">No friction data.</Text>
          )}
        </section>

        {/* 3. Condition worksheets */}
        <section className={styles.section}>
          <Title level={2}>Condition worksheets</Title>
          {sortedCardItems.length > 0 ? (
            <div className={styles.worksheetsList}>
              {sortedCardItems.map((item, index) => {
                const q = questionById.get(item.question_id);
                const currentLabel = getOptionLabel(q, q?.current_answer?.score);
                const futureLabel = getOptionLabel(q, item.future_answer_score ?? undefined);
                return (
                  <Card key={item.id} size="small" className={styles.worksheetCard} title={`Priority ${index + 1}`}>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Question (current state):</Text>
                      <Text>{q?.text ?? item.reason ?? item.question_id}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Current answer:</Text>
                      <Text>{currentLabel}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Future condition:</Text>
                      <Text>{item.future_condition || '—'}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Future answer:</Text>
                      <Text>{futureLabel}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Blocking assumption:</Text>
                      <Text>{item.assumption_text || '—'}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Change action (plank):</Text>
                      <Text>{item.plank_description || '—'}</Text>
                    </div>
                    <div className={styles.worksheetRow}>
                      <Text type="secondary">Resource / duration:</Text>
                      <Text>{[item.resource_assignment, item.estimated_duration].filter(Boolean).join(' · ') || '—'}</Text>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Text type="secondary">No condition worksheets. Complete Define Conditions step.</Text>
          )}
        </section>

        {/* 4. Bridge card */}
        <section className={styles.section}>
          <Title level={2}>Bridge card</Title>
          {bridgeCardObjective ? (
            <div className={styles.bridgeCardSection}>
              <BridgeCard objective={bridgeCardObjective} />
            </div>
          ) : (
            <Text type="secondary">No bridge card data.</Text>
          )}
        </section>

        {/* 5. Session analysis (editable) */}
        <section className={`${styles.section} ${styles.sectionSessionAnalysis}`}>
          <Title level={2}>Session analysis</Title>
          <p className={styles.sectionDescription}>Your write-up or analysis of this bridge session. It will be included in the report when you print or export to PDF.</p>
          {editingAnalysis ? (
            <div className={styles.sessionAnalysisEdit}>
              <Input.TextArea
                value={analysisDraft}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAnalysisDraft(e.target.value)}
                rows={8}
                placeholder="Enter your session analysis or write-up..."
              />
              <div className={styles.sessionAnalysisActions}>
                <Button type="primary" onClick={saveSessionAnalysis} loading={savingAnalysis}>
                  Save
                </Button>
                <Button onClick={cancelEditingAnalysis} disabled={savingAnalysis}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.sessionAnalysisContent}>
              {sessionAnalysis != null && sessionAnalysis.trim() !== '' ? (
                <>
                  <div className={styles.sessionAnalysisText} style={{ whiteSpace: 'pre-wrap' }}>
                    {sessionAnalysis}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Button size="small" onClick={startEditingAnalysis}>
                      Edit
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Text type="secondary">No analysis yet.</Text>
                  <div style={{ marginTop: 12 }}>
                    <Button size="small" type="primary" onClick={startEditingAnalysis}>
                      Add session analysis
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BridgeSessionReportPage;
